import { useState } from "react";
import { ArrowLeft, Pencil, Sparkles } from "lucide-react";

import { TemplateIcon } from "@/components/contract-intelligence/templates/TemplateIcon";
import { Alert, Button, Card, Progress, ScrollArea } from "@/components/ui";
import { getTemplateFlow } from "@/mockData/contractTemplates";
import type { ContractTemplate, ContractTemplateAnswers } from "@/types/contractIntelligence.types";

interface ContractTemplateReviewProps {
  template: ContractTemplate;
  answers: ContractTemplateAnswers;
  onBack: () => void;
  onCreate: () => void;
}

function formatAnswer(value: string | string[] | undefined): React.ReactNode {
  if (!value) return <em className="text-muted-foreground">Not provided</em>;
  return Array.isArray(value) ? value.join(", ") : value;
}

export function ContractTemplateReview({ template, answers, onBack, onCreate }: ContractTemplateReviewProps) {
  const [generating, setGenerating] = useState(false);
  const flow = getTemplateFlow(template.id);

  const handleCreate = () => {
    setGenerating(true);
    window.setTimeout(() => onCreate(), 1600);
  };

  if (generating) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-5 bg-background p-8">
        <span className="flex size-16 items-center justify-center rounded-2xl bg-gradient-to-br from-primary to-violet-500 shadow-lg shadow-primary/30">
          <Sparkles className="size-7 text-white" aria-hidden="true" />
        </span>
        <div className="text-center">
          <h2 className="text-lg font-semibold">Drafting your contract</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Ari is filling sections, attaching standard terms, and running risk checks…
          </p>
        </div>
        <Progress value={40} className="h-1 w-60 animate-pulse" />
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      <header className="flex shrink-0 items-center gap-3 border-b border-border px-6 py-4">
        <Button type="button" variant="outline" size="icon" className="size-8" onClick={onBack} aria-label="Back">
          <ArrowLeft className="size-4" />
        </Button>
        <div>
          <p className="text-[11px] font-semibold tracking-wide text-primary uppercase">Step 3 — Review</p>
          <h1 className="text-lg font-semibold tracking-tight">Review answers, then generate</h1>
        </div>
      </header>

      <ScrollArea className="min-h-0 flex-1">
        <div className="mx-auto max-w-2xl space-y-4 p-6 pb-10">
          <Card className="gap-0 p-4">
            <div className="mb-4 flex items-center gap-2.5">
              <span
                className="flex size-9 items-center justify-center rounded-lg text-white"
                style={{ background: `linear-gradient(135deg, ${template.color}, ${template.color}cc)` }}
              >
                <TemplateIcon name={template.icon} className="size-4" />
              </span>
              <div>
                <p className="text-sm font-semibold">{template.name}</p>
                <p className="text-[11px] text-muted-foreground">
                  {template.pages} pages · {template.sections.length} sections · ~{flow.length} answers
                </p>
              </div>
            </div>

            {flow.map((field, index) => (
              <div
                key={field.id}
                className="flex gap-3 border-t border-border py-2.5 first:border-t-0"
              >
                <span className="w-7 shrink-0 font-mono text-[11px] text-muted-foreground">
                  {(index + 1).toString().padStart(2, "0")}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-[11px] text-muted-foreground">{field.question}</p>
                  <p className="mt-0.5 text-[13px] font-medium">{formatAnswer(answers[field.id])}</p>
                </div>
                <Button type="button" variant="outline" size="sm" className="h-7 shrink-0 self-center text-[11px]" onClick={onBack}>
                  <Pencil className="size-3" aria-hidden="true" />
                  Edit
                </Button>
              </div>
            ))}
          </Card>

          <Alert className="border-primary/40 bg-primary/10">
            <Sparkles className="size-4 text-primary" aria-hidden="true" />
            <div className="text-xs leading-relaxed">
              <span className="font-semibold text-primary">Ari&apos;s pre-flight check: </span>
              Terms look standard. Ari will attach {template.pages - template.sections.length} pages of boilerplate,
              run risk analysis, and open the draft in the editor for redlining.
            </div>
          </Alert>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onBack}>
              Back to questions
            </Button>
            <Button type="button" onClick={handleCreate}>
              <Sparkles className="size-3.5" aria-hidden="true" />
              Generate contract
            </Button>
          </div>
        </div>
      </ScrollArea>
    </div>
  );
}
